import { useMutation } from "@apollo/client";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Avatar from "components/core/Avatar";
import translateServerErrorIntoFriendlyMessage from "components/discussions/form/translateServerErrorIntoFriendlyMessage";
import Editor from "components/editor/Editor";
import InlineErrorSpan from "components/shared/form/InlineErrorSpan";
import { Button } from "components/ui/Button";
import classNames from "components/ui/classNames";

import ReplyToPostMutation from "lib/graphql/mutations/engagement/ReplyToPostMutation";
import type {
  IReplyToPostMutation,
  IReplyToPostMutationVariables,
} from "lib/graphql/mutations/engagement/__generated__/ReplyToPostMutation.generated";
import PostRepliesQuery from "lib/graphql/queries/engagement/PostRepliesQuery";
import PostsQuery from "lib/graphql/queries/engagement/PostsQuery";
import { ButtonGroup, LinkButton } from "lib/miolo";
import type { ISelfieQuery } from "lib/queries/__generated__/SelfieQuery.generated";
import type { IPostContextType } from "types/graphqlTypes";
import type { ReplyPostData } from "components/forum/form";
import { replyPostInputFormDataShape } from "components/forum/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormMessage } from "components/ui/Form";

interface IForumReplyCardProps {
  selfie: NonNullable<ISelfieQuery["selfie"]>;
  shortId: string;
  postId: string;
  contextKey?: string | null;
  contextType: IPostContextType;
  onCancel: () => void;
}

const ForumReplyCard = React.forwardRef<HTMLDivElement | null, IForumReplyCardProps>(
  ({ selfie, contextKey, contextType, shortId, postId, onCancel }, ref) => {
    const [replyToPost] = useMutation<IReplyToPostMutation, IReplyToPostMutationVariables>(ReplyToPostMutation, {
      refetchQueries: [
        {
          query: PostRepliesQuery,
          variables: {
            postShortId: shortId,
            cursor: null,
          },
        },
        {
          query: PostsQuery,
          variables: {
            contextKey,
            contextType,
            limit: 30,
            cursor: null,
          },
        },
      ],
    });

    const intl = useIntl();

    const form = useForm<ReplyPostData>({
      mode: "onSubmit",
      resolver: zodResolver(replyPostInputFormDataShape(intl)),
      defaultValues: {
        bodyAsJson: "",
        bodyAsPlainText: "",
      },
    });

    const {
      formState: { touchedFields, errors, isSubmitting },
    } = form;

    const handleSubmit = async ({ bodyAsJson, bodyAsPlainText }: ReplyPostData) => {
      try {
        await replyToPost({
          variables: {
            postId,
            bodyAsJson,
            bodyAsPlainText,
          },
        });

        setTimeout(() => {
          form.reset();
          onCancel();
        }, 1);
      } catch (err) {
        form.setError("root.serverError", {
          message: translateServerErrorIntoFriendlyMessage(
            err,
            intl,
            intl.formatMessage({
              defaultMessage: "Não foi possível enviar sua mensagem agora. Tente novamente mais tarde.",
              id: "AsTfN9",
            }),
          ),
        });
      }
    };

    return (
      <div
        ref={ref}
        className="col-span-4 space-y-5 rounded border border-neutral-50 bg-white p-8 shadow md:col-span-9"
      >
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-5 gap-y-4 font-aeonik">
          <Avatar
            key={selfie.firstName}
            src={selfie.pictureUrl}
            hashValue={selfie._id}
            alt={selfie.firstName}
            size={42}
            className="text-2xl"
          />

          <div className="flex flex-col">
            <span className="w-max text-sm font-bold text-gray-800">{selfie.fullName}</span>
            {selfie.isSiteAdmin && (
              <span className="text-xs text-gray-500">
                <FormattedMessage description="Tag de Moderador no fórum" defaultMessage="Moderador" id="hQqTzK" />
              </span>
            )}
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-y-5">
            <Editor
              className="h-fit w-full"
              onChange={(bodyAsJson, bodyAsPlainText) => {
                form.setValue("bodyAsJson", bodyAsJson);
                form.setValue("bodyAsPlainText", bodyAsPlainText);
              }}
              onSubmit={() => {
                form.handleSubmit(handleSubmit)();
              }}
            />

            <div>
              {touchedFields.bodyAsJson && errors.bodyAsJson && (
                <InlineErrorSpan>{errors.bodyAsJson.message || ""}</InlineErrorSpan>
              )}
            </div>

            <FormMessage asRootError={true} />

            <div className="flex w-full justify-end">
              <ButtonGroup stack="horizontally" spacing={20}>
                <LinkButton
                  disabled={isSubmitting}
                  variant="black"
                  onClick={() => {
                    form.reset();
                    onCancel();
                  }}
                >
                  <FormattedMessage defaultMessage="Cancelar" id="nZLeaQ" />
                </LinkButton>

                <Button
                  className={classNames(
                    "flex h-9 w-auto select-none items-center justify-center overflow-visible rounded border-none",
                    "bg-brand px-3 text-sm text-white shadow-md hover:bg-brand/50 focus:outline-none",
                    {
                      "cursor-default bg-brand/50": isSubmitting,
                    },
                  )}
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <FormattedMessage defaultMessage="Aguarde..." id="VZazm7" />
                  ) : (
                    <FormattedMessage defaultMessage="Enviar" id="hKJZJR" />
                  )}
                </Button>
              </ButtonGroup>
            </div>
          </form>
        </Form>
      </div>
    );
  },
);

export default ForumReplyCard;
